import { FormEvent, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import type { OrgApiKey } from 'chat-shared'
import { api } from '../api/client.js'
import { AdminCard, AdminCardFooter, AdminTableEmpty, adminTableSx } from './AdminUi.js'

const formatDate = (iso: string | undefined): string =>
  iso ? new Date(iso).toLocaleString('de-DE') : '—'

type OrgApiKeysSectionProps = {
  orgId: string
}

// API-Schlüssel der Organisation für Drittsysteme (z. B. Teams-Bot, eigene
// Integrationen gegen /api/v1). Nur für Owner/Admin sichtbar — der Server
// erzwingt das ohnehin (siehe apps/chat-api/src/http/orgApiKeys.ts).
export const OrgApiKeysSection = ({ orgId }: OrgApiKeysSectionProps) => {
  const [keys, setKeys] = useState<Array<OrgApiKey>>([])
  const [name, setName] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  // Klartext-Schlüssel aus der Create-Antwort — wird genau einmal angezeigt.
  const [createdKey, setCreatedKey] = useState<{ name: string; key: string } | undefined>(undefined)
  const [revokeTarget, setRevokeTarget] = useState<OrgApiKey | undefined>(undefined)
  const [copied, setCopied] = useState(false)

  const reload = () => {
    api.listOrgApiKeys(orgId).then(setKeys).catch(() => setKeys([]))
  }

  useEffect(() => {
    reload()
    setError(undefined)
  }, [orgId])

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault()
    setError(undefined)
    try {
      const { apiKey, key } = await api.createOrgApiKey(orgId, name)
      setCreatedKey({ name: apiKey.name, key })
      setCopied(false)
      setName('')
      reload()
    } catch {
      setError('API-Schlüssel anlegen fehlgeschlagen')
    }
  }

  const handleRevoke = async () => {
    if (!revokeTarget) return
    setError(undefined)
    try {
      await api.revokeOrgApiKey(orgId, revokeTarget.id)
      setRevokeTarget(undefined)
      reload()
    } catch {
      setError('Widerrufen fehlgeschlagen')
    }
  }

  const handleCopy = async () => {
    if (!createdKey) return
    await navigator.clipboard.writeText(createdKey.key)
    setCopied(true)
  }

  return (
    <Stack gap={3}>
      {error && (
        <Alert severity="error" onClose={() => setError(undefined)}>
          {error}
        </Alert>
      )}
      <AdminCard
        title={`API-Schlüssel (${keys.length})`}
        description="Für Drittsysteme wie den Teams-Bot oder eigene Integrationen gegen /api/v1."
      >
        <Table size="small" sx={adminTableSx}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Schlüssel</TableCell>
            <TableCell>Erstellt</TableCell>
            <TableCell>Zuletzt benutzt</TableCell>
            <TableCell>Status</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {keys.map((key) => (
            <TableRow key={key.id}>
              <TableCell>{key.name}</TableCell>
              <TableCell>
                <code>{key.prefix}</code>
              </TableCell>
              <TableCell>{formatDate(key.createdAt)}</TableCell>
              <TableCell>{formatDate(key.lastUsedAt)}</TableCell>
              <TableCell>
                {key.revokedAt ? (
                  <Chip size="small" label="Widerrufen" color="default" />
                ) : (
                  <Chip size="small" label="Aktiv" color="success" />
                )}
              </TableCell>
              <TableCell>
                {!key.revokedAt && (
                  <IconButton size="small" onClick={() => setRevokeTarget(key)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
          {keys.length === 0 && (
            <AdminTableEmpty colSpan={6}>
              Noch keine API-Schlüssel für diese Organisation.
            </AdminTableEmpty>
          )}
        </TableBody>
      </Table>
      <AdminCardFooter>
        <Box component="form" onSubmit={handleCreate} display="flex" gap={1} maxWidth={480}>
          <TextField
            size="small"
            label="Name (z. B. Zapier-Integration)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <Button type="submit" variant="contained">
            Anlegen
          </Button>
        </Box>
      </AdminCardFooter>
      </AdminCard>

      <Dialog open={Boolean(createdKey)} maxWidth="sm" fullWidth>
        <DialogTitle>API-Schlüssel „{createdKey?.name}“ erstellt</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Dieser Schlüssel wird nur einmal angezeigt. Kopiere ihn jetzt und bewahre ihn sicher auf.
          </Alert>
          <Stack direction="row" gap={1} alignItems="center">
            <TextField
              size="small"
              fullWidth
              value={createdKey?.key ?? ''}
              slotProps={{ input: { readOnly: true, sx: { fontFamily: 'monospace' } } }}
            />
            <IconButton onClick={() => void handleCopy()}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Stack>
          {copied && (
            <Typography variant="body2" color="success.main" mt={1}>
              In die Zwischenablage kopiert.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedKey(undefined)}>
            Fertig
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(revokeTarget)} onClose={() => setRevokeTarget(undefined)}>
        <DialogTitle>Schlüssel widerrufen?</DialogTitle>
        <DialogContent>
          „{revokeTarget?.name}“ wird dauerhaft widerrufen — Systeme, die diesen Schlüssel verwenden,
          verlieren sofort den Zugriff.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(undefined)}>Abbrechen</Button>
          <Button color="error" onClick={() => void handleRevoke()}>
            Widerrufen
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
